import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  getServices, 
  createService, 
  updateService, 
  deleteService, 
  checkServiceUsage,
  Service 
} from '../../services/serviceService';
import Footer from '../../components/Footer';

export default function Services() {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form state
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      setLoading(true);
      const data = await getServices();
      setServices(data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Service name is required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const serviceData = {
        name: name.trim(),
        description: description.trim() || undefined,
      };

      if (isEditing && editingId) {
        await updateService(editingId, serviceData);
        setSuccess('Service updated successfully');
      } else {
        await createService(serviceData);
        setSuccess('Service created successfully');
      }

      resetForm();
      await loadServices();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${isEditing ? 'update' : 'create'} service`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (service: Service) => {
    setIsEditing(true);
    setEditingId(service.id);
    setName(service.name);
    setDescription(service.description || '');
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (service: Service) => {
    try {
      // Check if service is in use
      const usage = await checkServiceUsage(service.id);
      
      if (usage.isUsed) {
        setError(
          `Cannot delete "${service.name}" because it is assigned to ${usage.bookingCount} booking(s). ` +
          'Please remove the service from all bookings first.'
        );
        return;
      }

      if (!window.confirm(`Are you sure you want to delete "${service.name}"?`)) {
        return;
      }

      setLoading(true);
      await deleteService(service.id);
      setSuccess('Service deleted successfully');
      await loadServices();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete service';
      
      // Check if error indicates service is in use
      if (errorMessage.toLowerCase().includes('in use') || 
          errorMessage.toLowerCase().includes('assigned') ||
          errorMessage.toLowerCase().includes('booking')) {
        setError(
          `Cannot delete "${service.name}" because it is currently assigned to bookings. ` +
          'Please remove the service from all bookings first.'
        );
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setIsEditing(false);
    setEditingId(null);
    setName('');
    setDescription('');
    setShowForm(false);
    setError('');
  };

  return (
    <div className="p-6 pb-20 bg-background min-h-screen font-sans">
      {/* Navigation */}
      <div className="flex items-center space-x-2 mb-4">
        <Link 
          to="/admin/dashboard" 
          className="text-primary hover:underline"
        >
          Admin Dashboard
        </Link>
        <span className="text-secondary">/</span>
        <span className="text-secondary">Manage Services</span>
      </div>

      <h1 className="text-3xl font-semibold mb-6 text-secondary font-sans">Manage Services</h1>

      {/* Success Message */}
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
          <button 
            onClick={() => setError('')} 
            className="float-right font-bold"
          >
            ×
          </button>
        </div>
      )}

      {/* Add/Edit Form Toggle */}
      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="btn-primary mb-6"
        >
          + Add New Service
        </button>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white shadow rounded p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 text-secondary">
            {isEditing ? 'Edit Service' : 'Add New Service'}
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-secondary mb-1">
                Service Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="e.g., Bridal Makeup, Party Makeup"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary mb-1">
                Description (Optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Brief description of the service"
                rows={3}
              />
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
              >
                {loading ? 'Saving...' : isEditing ? 'Update Service' : 'Add Service'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Services List */}
      <div className="bg-white shadow rounded overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-highlight">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-secondary uppercase tracking-wider">
                Service Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-secondary uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-secondary uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading && services.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-6 py-4 text-center text-secondary">
                  Loading services...
                </td>
              </tr>
            ) : services.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-6 py-4 text-center text-secondary">
                  No services found. Add your first service above.
                </td>
              </tr>
            ) : (
              services.map((service) => (
                <tr key={service.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {service.name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {service.description || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                    <button
                      onClick={() => handleEdit(service)}
                      className="text-primary hover:text-primary-dark mr-4"
                      disabled={loading}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(service)}
                      className="text-red-600 hover:text-red-900"
                      disabled={loading}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Footer />
    </div>
  );
}
